import React, { useMemo, useState } from "react";
import { FormDataType } from "../Form";
import ChatPanel from "./ChatPanel";
import ChatToggleButton from "./ChatToggleButton";
import useChatAgentIsEnabled from "./hooks/useIsChatAgentEnabled";

export interface CreateFormUpdateHandlerParams {
  setServerProvidedFormData: (data: FormDataType) => void;
  setDisabled: (disabled: boolean) => void;
  addSuccessToast: (message: string) => void;
}

export const createFormUpdateHandler = ({
  setServerProvidedFormData,
  setDisabled,
  addSuccessToast,
}: CreateFormUpdateHandlerParams) => {
  return (updates: any) => {
    if (!updates || typeof updates !== "object") {
      return;
    }

    setServerProvidedFormData(updates);
    setDisabled(false);
    addSuccessToast("Form updated by AI assistant");
  };
};

export interface UseSubmissionsChatAgentProps {
  isPublic: boolean;
  submissionTypeVersionId?: string;
  formData?: FormDataType;
  onFormUpdate?: (updates: any) => void;
}

export const useSubmissionsChatAgent = ({
  isPublic,
  submissionTypeVersionId,
  formData,
  onFormUpdate,
}: UseSubmissionsChatAgentProps) => {
  const isEnabled = useChatAgentIsEnabled();
  const [isOpen, setIsOpen] = useState(false);

  const ToggleButton = useMemo(() => {
    if (isPublic || !isEnabled) return null;
    return (
      <ChatToggleButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />
    );
  }, [isPublic, isEnabled, isOpen]);

  const Panel = useMemo(() => {
    if (!isEnabled || !submissionTypeVersionId || !formData || !onFormUpdate) {
      return null;
    }
    return (
      <ChatPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        submissionTypeVersionId={submissionTypeVersionId}
        formData={formData}
        onFormUpdate={onFormUpdate}
      />
    );
  }, [isEnabled, isOpen, submissionTypeVersionId, formData, onFormUpdate]);

  const isPanelOpen = isEnabled && isOpen && Boolean(submissionTypeVersionId);

  return {
    isEnabled,
    isOpen,
    setIsOpen,
    ChatToggleButton: ToggleButton,
    ChatPanel: Panel,
    isChatPanelOpen: isPanelOpen,
  } as const;
};

export default useSubmissionsChatAgent;
